import 'tippy.js/dist/tippy.css'
import 'whatwg-fetch'
import '../css/style.scss'

import Crisp from 'components/Crisp'
import CampaignRegistryPendingBar from 'components/ReusableComponents/CampaignRegistryPendingBar'
import HotelSetupPendingBar from 'components/ReusableComponents/HotelSetupPendingBar'
import { UnlockServiceProvider } from 'components/ReusableComponents/UnlockServiceDialog'
import PathChangeLogger from 'components/Utils/PathChangeLogger'
import URLprotocolHandler from 'components/Utils/URLprotocolHandler'
import useElectronMessageHandler from 'hooks/useElectronMessageHandler'
import useFirstRender from 'hooks/useFirstRender'
import usePlausible from 'hooks/usePlausible'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import { loginUser, logoutUser, noLoginInfoFound, savePrevPath } from '../actions'
import VersionChecker from '../hooks/versionChecker'
import LocalUserService from '../services/localUserService'
import EnvBar from './ReusableComponents/EnvBar/index'
import Routes from './Routes'
import ServiceWorkerHandler from './ServiceWorkerHandler/index'
import BillingContactDialog from './Utils/BillingContactDialog'
import FullScreenRealoder from './Utils/FullScreenReloder'
import HeaderContextProvider from './Utils/HeaderContentProvider'
import MobileDetect from './Utils/MobileDetect'
import NotAuthed from './Utils/NotAuthedBar'
import ToastProvider from './Utils/ToastProvider'

const pagesWithouthLogin = [
  '/login',
  '/magic-link',
  '/directInvoice',
  '/contract/',
  '/invoice-for-print',
  '/desktop-app'
]

const userService = new LocalUserService()

const App = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  const pathname = location.pathname

  const sessionExpired = useSelector(s => s.login.sessionExpired)
  const currentHotel = useSelector(s => s.login.currentHotel)
  const userLoggedIn = useSelector(s => s.login.userLoggedIn)
  const prevPath = useSelector(s => s.login.prevPath)
  const sessionId = useSelector(s => s.login.sessionId)

  const { init } = usePlausible()

  useFirstRender(() => {
    init()
  })

  useEffect(() => {
    function handleRedirect(event) {
      if (event.data.type === 'redirect') {
        history.push(event.data.url)
      }
    }

    window.addEventListener('message', handleRedirect)

    return () => {
      window.removeEventListener('message', handleRedirect)
    }
  }, [history])

  useElectronMessageHandler()

  useFirstRender(() => {
    const loginWithSavedUser = async savedUser => {
      const { user, userToken, userId, sessionId } = savedUser
      dispatch(loginUser(user, userToken, userId, sessionId))
    }

    const checkSavedUserInfo = () => {
      var savedUser = userService.getCurrentUserInfo()
      if (savedUser && savedUser.user) {
        loginWithSavedUser(savedUser)
      } else if (!location.pathname.includes('magic-link')) {
        dispatch(noLoginInfoFound())
        history.push('/login')
      }
    }

    const { pathname, search } = location

    if (pagesWithouthLogin.some(p => pathname.includes(p))) {
      return
    }

    if (
      !pathname.includes('/login') &&
      !pathname.includes('selectHotel') &&
      !pathname.startsWith('/invite/')
    ) {
      dispatch(savePrevPath(pathname + search))
    }

    checkSavedUserInfo()
  }, [location, dispatch, history])

  useEffect(() => {
    if (
      sessionExpired &&
      !pathname.includes('login') &&
      !pathname.includes('magic-link') &&
      !pathname.includes('directInvoice') &&
      !pathname.includes('invoice-for-print')
    ) {
      history.push('/login')
    }
  }, [history, pathname, sessionExpired])

  useEffect(() => {
    if (
      userLoggedIn &&
      !currentHotel &&
      !pathname.includes('selectHotel') &&
      !pathname.includes('magic-link') &&
      !pathname.includes('directInvoice') &&
      !pathname.includes('invoice-for-print')
    ) {
      history.replace('/selectHotel')
    }
  }, [currentHotel, userLoggedIn, history, pathname])

  useEffect(() => {
    if (pathname.includes('/login') && sessionExpired) {
      let info = userService.getCurrentUserInfo()
      if (info) {
        userService.removeCurrentUser()
      } else if (userLoggedIn) {
        dispatch(logoutUser())
      }
    }
  }, [dispatch, pathname, sessionExpired, userLoggedIn])

  useEffect(() => {
    if (userLoggedIn && currentHotel && pathname.includes('selectHotel')) {
      history.push(prevPath)
    }
  })

  return (
    <>
      <CampaignRegistryPendingBar />
      <HotelSetupPendingBar />
      <EnvBar />
      <ServiceWorkerHandler />
      <MobileDetect />
      <URLprotocolHandler />
      <VersionChecker />
      <Crisp />
      <ToastProvider>
        <UnlockServiceProvider>
          <NotAuthed />
          <PathChangeLogger />
          <BillingContactDialog />
          <FullScreenRealoder />
          <HeaderContextProvider>
            <Routes key={sessionId} />
          </HeaderContextProvider>
        </UnlockServiceProvider>
      </ToastProvider>
    </>
  )
}

export default App
